import {AnimatePresence, motion} from 'framer-motion';
import {
  DataStatus,
  DropdownGroup,
  DropdownItem,
  FormControl,
  FormField,
  Label,
  LabelActions,
  openDropdown,
  showNotification,
} from 'platform/components';
import {HStack, Link, VStack} from 'platform/foundation';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {
  ContextTemplateResponseBody,
  useGetContextTemplateListQuery,
  useRenderServiceOrderEmailTemplateMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {myProfileRoutes} from '@dms/routes';
import {
  AiComposerEditor,
  AiComposerAppliedStatus,
  AiMessageComposerPanel,
  AiComposerSelectionActions,
  getAiComposerEditorHighlights,
  EMAIL_DIALOG_BODY_MIN_ROWS,
  EmailBodyFormField,
  type EmailFormValues as FormValues,
  handleApiError,
  replaceVariables,
  templateBodyToEmailHtml,
  useAiMessageComposer,
  useEmailBodyImageUpload,
  useTemplatesByLocale,
  VARIABLE_REGEX,
} from '@dms/shared';

import {Nullish} from 'shared/web';

interface MessageFormFieldProps {
  formApi: UseFormReturn<FormValues>;
  control: FormControl<FormValues>;
  serviceCaseId: string;
  serviceOrderId?: string;
  preferredLocale?: string | Nullish;
  upload: ReturnType<typeof useEmailBodyImageUpload>;
}

export function MessageFormField(props: MessageFormFieldProps) {
  const [renderServiceOrderTemplate, {isLoading: isRenderServiceOrderTemplateInProgress}] =
    useRenderServiceOrderEmailTemplateMutation();

  const customerMessageId = useWatch({
    control: props.control,
    name: 'customerMessageId',
  });

  const {
    data: templates,
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
  } = useGetContextTemplateListQuery({
    type: 'email',
    context: 'service-order',
  });

  const onTemplateClick = (template: ContextTemplateResponseBody) => {
    if (!props.serviceOrderId) {
      showNotification.error();
      return;
    }

    renderServiceOrderTemplate({
      renderServiceOrderEmailTemplateRequestBody: {
        template: {
          subject: template.subject ?? '',
          body: template.body,
        },
        serviceCaseId: props.serviceCaseId,
        serviceOrderId: props.serviceOrderId,
      },
    })
      .unwrap()
      .then((response) => {
        props.formApi.setValue('subject', replaceVariables(response.subject));
        props.formApi.setValue('body', templateBodyToEmailHtml(response.body));
        if (VARIABLE_REGEX.test(response.subject) || VARIABLE_REGEX.test(response.body)) {
          showNotification.warning(i18n.t('general.notifications.notAllPlaceholdersFilled'));
        }
      })
      .catch(handleApiError);
  };

  const isCustomerMessage = customerMessageId?.includes('customer-message') ?? false;
  const templatesByLocale = useTemplatesByLocale(templates, props.preferredLocale);
  const [body, subject, selectedCustomerId, recipientAddress] = useWatch({
    control: props.control,
    name: ['body', 'subject', 'customerId', 'customerEmailAddress'],
  });

  const [editor, setEditor] = useState<AiComposerEditor>(null);

  const composer = useAiMessageComposer({
    channel: 'email',
    bodyFormat: 'HTML',
    draft: {body: body ?? '', subject: subject ?? undefined},
    record: {
      context: isNotNil(props.serviceOrderId) ? 'SERVICE_ORDER' : 'SERVICE_CASE',
      recordId: props.serviceOrderId ?? props.serviceCaseId,
      customerId: selectedCustomerId,
      recipientAddress,
    },
    editor,
    locale: props.preferredLocale,
    onApply: (draft) => {
      props.formApi.setValue('body', draft.body);
      if (isNotNil(draft.subject)) {
        props.formApi.setValue('subject', draft.subject);
      }
    },
  });

  const templateAction: LabelActions[number] = {
    title: i18n.t('entity.customer.labels.chooseEmailTemplate'),
    leftIcon: 'action/autorenew',
    isDisabled: isRenderServiceOrderTemplateInProgress,
    onClick: (event) =>
      openDropdown(
        event,
        <>
          <DataStatus isError={isTemplatesError} isLoading={isLoadingTemplates} />
          {templatesByLocale.isGrouped
            ? templatesByLocale.groups.map((group) => (
                <DropdownGroup key={group.key} label={group.label}>
                  {group.templates.map((template) => (
                    <DropdownItem
                      label={template.name}
                      key={template.id}
                      onClick={() => onTemplateClick(template)}
                    />
                  ))}
                </DropdownGroup>
              ))
            : templatesByLocale.templates.map((template) => (
                <DropdownItem
                  label={template.name}
                  key={template.id}
                  onClick={() => onTemplateClick(template)}
                />
              ))}
        </>,
        {
          isHeightLimited: true,
        }
      ),
  };

  const labelActions: LabelActions = [
    ...(isNotNil(composer.labelAction) ? [composer.labelAction] : []),
    ...(props.serviceOrderId ? [templateAction] : []),
  ];

  return (
    <AnimatePresence initial={false} mode="wait">
      {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
      {isCustomerMessage && (
        <motion.div
          initial={{height: 0, opacity: 0}}
          animate={{height: 'auto', opacity: 1}}
          exit={{height: 0, opacity: 0}}
          transition={{duration: 0.2, ease: 'easeInOut'}}
          style={{overflow: 'hidden'}}
        >
          <VStack spacing={4}>
            <FormField
              control={props.control}
              name="subject"
              type="text"
              label={i18n.t('general.labels.subject')}
              isRequired={isCustomerMessage}
            />

            <VStack spacing={1}>
              <EmailBodyFormField
                control={props.control}
                name="body"
                upload={props.upload}
                isDisabled={isRenderServiceOrderTemplateInProgress}
                labelActions={labelActions}
                onEditorReady={setEditor}
                selectionContent={
                  composer.isEnabled ? (
                    <AiComposerSelectionActions
                      controller={composer}
                      data-testid="sendAftersalesEmail"
                    />
                  ) : undefined
                }
                editorHighlights={getAiComposerEditorHighlights(composer)}
                onKeyDownCapture={composer.onEditorKeyDownCapture}
                minRows={EMAIL_DIALOG_BODY_MIN_ROWS}
                footerContent={
                  <AiComposerAppliedStatus
                    controller={composer}
                    data-testid="sendAftersalesEmail"
                  />
                }
                editorContent={
                  <AiMessageComposerPanel controller={composer} data-testid="sendAftersalesEmail" />
                }
                isRequired={isCustomerMessage}
                data-testid="sendAftersalesEmail"
              />
              <HStack justify="space-between" width="100%">
                <Label>{i18n.t('entity.customer.labels.messageWithSignature')}</Label>
                <Link
                  leftIcon="image/edit"
                  size="xSmall"
                  href={myProfileRoutes.emailSignature}
                  target="_blank"
                >
                  {i18n.t('entity.customer.labels.goToUserSignature')}
                </Link>
              </HStack>
            </VStack>
          </VStack>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
